import type { LeapBotErrorKind } from '../interfaces/leapbot.interface';

/** Spanish copy shown by the LeapBot chat when a request fails, one message per error kind. */
export const LEAPBOT_ERROR_COPY: Readonly<Record<LeapBotErrorKind, string>> = {
  session_expired: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  forbidden: 'No tienes permiso para usar LeapBot.',
  payload_too_large: 'Los archivos superan el tamaño permitido.',
  rate_limited: 'Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.',
  unreachable: 'No se pudo contactar a LeapBot. Revisa tu conexión e inténtalo de nuevo.',
  rejected: 'LeapBot no pudo procesar tu mensaje.',
};

/** Shown when the answer channel cannot be opened after all retries. */
export const LEAPBOT_STREAM_UNAVAILABLE_COPY =
  'Tu mensaje fue enviado, pero no se pudo abrir el canal de respuesta. Intenta de nuevo; si persiste, prueba desde otra red o avisa a soporte.';

/** Shown when no event arrives for too long after a message was sent. */
export const LEAPBOT_SILENCE_COPY = 'LeapBot no respondió. Envía tu mensaje de nuevo.';

/** Spanish copy of the chat service: status, recording and attachment notices. */
export const LEAPBOT_NOTICE_COPY = {
  recordingFailed: 'No se pudo grabar la pantalla. Inténtalo de nuevo.',
  recordingLimit: 'La grabación se detuvo al llegar a 5 minutos.',
  recordUnsupported: 'Tu navegador no permite grabar la pantalla',
  recordNoRoom: 'Quita archivos para grabar: máximo 5 por mensaje',
  statusConnecting: 'Conectando…',
  statusTyping: 'Escribiendo…',
  statusOffline: 'Sin conexión',
  statusOnline: 'En línea',
  filesRejected: (count: number): string => `LeapBot rechazó ${count} archivo(s). Los demás se enviaron.`,
} as const;

/** Milliseconds without any event after which a pending answer is given up. */
export const LEAPBOT_SILENCE_TIMEOUT_MS = 120_000;

/** Milliseconds without a new token after which a streamed answer is considered complete. */
export const LEAPBOT_STREAM_IDLE_MS = 3_000;

/** Backoff, in milliseconds, before each of the answer-channel reconnection attempts. */
export const LEAPBOT_RETRY_DELAYS_MS: readonly number[] = [1_500, 3_000, 4_500];

/** Spanish labels of the LeapBot chat, in one place so the copy is easy to review. */
export const LEAPBOT_LABELS = {
  title: 'LeapBot',
  panelName: 'Chat de LeapBot',
  openChat: 'Abrir chat de LeapBot',
  closeChat: 'Cerrar chat',
  unavailable: 'LeapBot no está disponible',
  newConversation: 'Nueva conversación',
  history: 'Historial de conversaciones',
  historyEmpty: 'Aún no tienes conversaciones.',
  deleteConversation: 'Eliminar conversación',
  deleteConfirm: '¿Eliminar esta conversación?',
  confirmDelete: 'Eliminar',
  cancel: 'Cancelar',
  emptyChat: '¿En qué puedo ayudarte?',
  inputLabel: 'Mensaje para LeapBot',
  placeholder: 'Escribe tu pregunta...',
  send: 'Enviar mensaje',
  stop: 'Detener respuesta',
  typing: 'LeapBot está escribiendo…',
  truncated: 'Respuesta cortada',
  hello: 'Hola',
  intro: 'Soy LeapBot. Resuelvo dudas sobre Instaleap y puedo crear tickets de Jira por ti.',
  disclaimer: 'LeapBot puede equivocarse. Revisa la información importante.',
  attach: 'Adjuntar archivos',
  attachedFiles: 'Archivos adjuntos',
  dropHere: 'Suelta los archivos aquí',
  viewFile: 'Ver',
  removeFile: 'Quitar',
  closePreview: 'Cerrar vista previa',
  previewTitle: 'Vista previa',
  recordScreen: 'Grabar pantalla',
  recording: 'Grabando',
  stopRecording: 'Detener grabación',
  stopRecordingShort: 'Detener',
  startRecording: 'Empezar a grabar',
  recordNotice:
    'Se grabará lo que compartas, incluida la consola y las peticiones de red de Fury. Nada se envía hasta que envíes el mensaje.',
  recordNoticeLabel: 'Aviso de grabación',
  recordSuggestion: {
    title: 'Reportar un problema con evidencia',
    hint: 'Graba tu pantalla y adjunto los registros',
    icon: 'video',
  },
  soundMute: 'Silenciar sonidos',
  soundUnmute: 'Activar sonidos',
  composerHint: 'Enter envía · Shift+Enter agrega una línea',
  copy: 'Copiar',
  copied: 'Copiado',
  copyFailed: 'No se pudo copiar',
  copyKey: 'Copiar clave',
  openInJira: 'Abrir en Jira',
  retry: 'Reintentar',
  continue: 'Continuar',
  historySearch: 'Buscar conversaciones',
  historyNoMatch: 'No hay conversaciones que coincidan.',
  sources: 'Fuentes',
  suggestions: [
    { title: '¿Cómo funciona el reembolso?', hint: 'Busca en la base de conocimiento', message: '¿Cómo funciona el reembolso?', icon: 'book-open' },
    { title: 'Crear un ticket en Jira', hint: 'Te pido los datos que falten', message: 'Quiero crear un ticket en Jira.', icon: 'ticket-check' },
  ],
  toolPrefix: 'Usando herramienta:',
  authorizedJira: 'Ya autoricé',
  connection: {
    connected: 'Conectado',
    connecting: 'Conectando…',
    disconnected: 'Desconectado',
    error: 'Error de conexión',
  },
} as const;
